/**
 * Build a flat array of question objects (including follow-ups),
 * plus factor and aggregate-factor follow-up items.
 *
 * @param {object} surveyJson - The entire survey definition
 * @param {string} language - Optional language code, e.g. "EN" or "FR".
 * @returns {Array} A flat array of "question objects".
 */
export function buildQuestionList(surveyJson, language = "EN") {
  const {
    questions = [],
    globalDictionary = {},
    factors = [],
    aggregateFactors = []
  } = surveyJson;

  // We'll accumulate everything here
  const finalList = [];
  
  // Store end_screen questions separately to add them at the very end
  const endScreenQuestions = [];

  // Simple translator using globalDictionary:
  function t(originalText) {
    if (!originalText) return "";
    const dictEntry = globalDictionary[originalText];
    if (dictEntry && dictEntry[language]) {
      return dictEntry[language];
    }
    return originalText;
  }

  // 1) Build the base question list (including item-level follow-ups)
  questions.forEach((q) => {
    const { uniqueId, type, properties = {}, conditionals = {} } = q;

    const baseQuestion = {
      uniqueId,
      type,
      question: properties.question ? t(properties.question) : "",
      description: properties.description ? t(properties.description) : "",
      qualifier: properties.qualifier ? t(properties.qualifier) : "",
      // for convenience store properties with translated options
      properties: {
        ...properties,
        ...(properties.lowLabel && { lowLabel: t(properties.lowLabel) }),
        ...(properties.highLabel && { highLabel: t(properties.highLabel) }),
        ...(properties.keys && {
          blocks: properties.keys.map((block) => ({
            ...block,
            ...(block.content && { content: t(block.content) }),
            ...(block.label && { label: t(block.label) }),
          })),
        }),
        ...(properties.options && {
          options: properties.options.map((opt) => t(opt)),
        }),
      },
      skip: false,
      hasScore: q?.conditionalConfig?.hasScore,
      skipConditions: [],
      followupConditions: [],
    };

    // Look for question-level conditionals (skip/followup)
    const conds = conditionals.conditionals || [];
    const followUpsToAdd = [];
    conds.forEach((cond) => {
      if (cond.type === "skip") {
        // skip if item-2 answer > 4, etc.
        baseQuestion.skipConditions.push({
          operator: cond.operator,
          threshold: cond.threshold,
          previousQuestion: cond.previousQuestion,
        });
      } else if (cond.type === "followup") {
        // Create a separate follow-up item
        const followupQuestion = {
          uniqueId: `${uniqueId}-followup-${cond.id}`,
          type: "followup",
          label: t(cond.followupText) || "(Follow-up)",
          parentId: uniqueId,
          skip: true, // hidden by default until user's answer triggers it
          skipConditions: [],
          unskipCondition: {
            operator: cond.operator,
            threshold: cond.threshold,
            parentQuestionId: uniqueId,
          },
          aiDriven: cond.aiDriven || false,
          aiPrompt: t(cond.aiPrompt) || "",
        };
        followUpsToAdd.push(followupQuestion);
      }
    });

    // If this is an end_screen question, store it separately
    if (type === "end_screen") {
      endScreenQuestions.push(baseQuestion);
      
      // Still add follow-ups to end screen questions if they exist
      // (though this might be uncommon)
      followUpsToAdd.forEach((fu) => endScreenQuestions.push(fu));
    } else {
      // Push the base question to main list
      finalList.push(baseQuestion);
      
      // Push any item-level follow-ups
      followUpsToAdd.forEach((fu) => finalList.push(fu));
    }
  });

  // 2) Append factor-level follow-ups
  factors.forEach((factor) => {
    const { factorId, title, description, questionIds = [], followUps = [] } = factor;
    followUps.forEach((fup) => {
      if (fup.conditionType === "averageScore") {
        const num = fup.numberOfFollowUps || 1;
        for (let i = 0; i < num; i++) {
          const factorFollowupId = `${factorId}-followup-${fup.followUpId}-${i}`;
          const factorFollowup = {
            uniqueId: factorFollowupId,
            type: "factorFollowup",
            skip: true,
            skipConditions: [
              {
                conditionType: "factorAverage",
                factorId: factorId,
                operator: fup.operator,
                threshold: fup.value,
              }
            ],
            aiDriven: !!fup.isAiDriven,
            aiPrompt: t(fup.aiPrompt) || "",
            label: t(title),
            factorTitle: t(title),
            factorDescription: t(description),
          };
          finalList.push(factorFollowup);
        }
      }
    });
  });

  // 3) Append aggregate factor follow-ups
  aggregateFactors.forEach((agg) => {
    const {
      followUpId,
      isAiDriven,
      triggerType,
      numberOfFollowUps = 1,
      aiPrompt,
      followUpQuestion
    } = agg;

    const translatedFollowUpQuestion = t(followUpQuestion) || "";
    const translatedAiPrompt = t(aiPrompt) || "";

    for (let i = 0; i < numberOfFollowUps; i++) {
      const aggFollowupId = `aggFollowup-${followUpId}-${i}`;
      const aggItem = {
        uniqueId: aggFollowupId,
        type: "aggregateFactorFollowup",
        skip: true,
        skipConditions: [
          {
            conditionType: triggerType,
          }
        ],
        aiDriven: !!isAiDriven,
        aiPrompt: translatedAiPrompt,
        label: translatedFollowUpQuestion, 
      };
      finalList.push(aggItem);
    }
  });

  // 3.5) Append global AI follow-ups based on aiOptions
  if (surveyJson.aiOptions && surveyJson.aiOptions.agentActive) {
    const maxQuestions = surveyJson.aiOptions.maxQuestions || 0;
    for (let i = 0; i < maxQuestions; i++) {
      const aiFollowupId = `aiFollowup-${i}`;
      const aiFollowup = {
        uniqueId: aiFollowupId,
        type: "aiFollowup",
        skip: false,
        aiDriven: true,
        label: t("AI Follow-up Question"),
        properties: surveyJson.aiOptions
      };
      finalList.push(aiFollowup);
    }
  }

  // 4) Append end_screen questions at the very end
  endScreenQuestions.forEach(endQ => {
    finalList.push(endQ);
  });

  return finalList;
}